/**
 * Combat Action Rules — SINGLE SOURCE OF TRUTH for move legality.
 *
 * Pure, RNG-free logic that decides which combat actions (attack / magic /
 * dodge / recover) are currently available, and why a blocked one is not.
 *
 * Consumed by:
 * - Backend rejection: `validateMove` (pvpBattleService.ts) — rejects on submit.
 * - Backend coercion: `getRedirectedAction` + `getActionLockFallback`
 *   (battleEngine.ts) — pick a legal redirect target (RNG stays in the backend,
 *   which only supplies the selection over the candidates produced here).
 * - Frontend: `FighterPanel` (PvpBattleComponents.tsx) — disables skill buttons.
 *
 * Because this is the only place the rules live, the frontend, the submit
 * validator, and the turn-resolution coercion can never silently diverge again.
 */

import type { CombatAction } from '../types/pvpTypes.js';
import { ACTION_LOCK_LIMITS } from '../constants/battleConstants.js';
import { isActionUnlocked } from './levelUnlocks.js';

/** Combat actions in their canonical order — preserved so backend RNG selection over candidates stays deterministic. */
export const ALL_COMBAT_ACTIONS: readonly CombatAction[] = [
  'attack',
  'magic',
  'dodge',
  'recover',
] as const;

/**
 * Outcome of one gesture met by another, from the first gesture's point of view.
 * `mirror` is both sides picking the same gesture — not a loss for either.
 */
export type GestureOutcome = 'win' | 'lose' | 'mirror';

/**
 * Which gestures each gesture beats — the whole wheel, in one table.
 *
 * Attack > Magic > Dodge > Attack is the rock-paper-scissors triangle. Recover sits
 * outside it and beats all three, which is why it is capped at 1-3 uses per battle.
 *
 * This table is the ONLY definition of who wins an exchange: `battleEngine.resolveTurn`
 * reads it, and the Battle Cards page renders the matrix straight from it, so the rules
 * a player is shown cannot drift from the rules the engine runs.
 */
const GESTURE_BEATS: Record<CombatAction, readonly CombatAction[]> = {
  attack: ['magic'],
  magic: ['dodge'],
  dodge: ['attack'],
  recover: ['attack', 'magic', 'dodge'],
};

/**
 * Who takes the exchange when `mine` meets `theirs` — pure wheel only.
 *
 * Deliberately excludes everything situational (stun, precise attack, forced/blocked
 * actions, the poker tiebreak): those are modifiers the engine layers on top of this
 * result, not part of the wheel itself.
 */
export function getGestureOutcome(mine: CombatAction, theirs: CombatAction): GestureOutcome {
  if (mine === theirs) return 'mirror';
  return GESTURE_BEATS[mine].includes(theirs) ? 'win' : 'lose';
}

export type ActionUnavailableReason =
  | 'stunned'
  | 'level-locked'
  | 'no-magic'
  | 'recover-exhausted'
  | 'dodge-streak'
  | 'blocked'
  | 'action-locked'
  | 'no-cards';

/**
 * Narrow structural subset of state needed to decide action legality.
 * Both the backend `PlayerState` and the serialized frontend combatant
 * structurally satisfy this — no coupling to the full battle state.
 */
export interface CombatActionState {
  level: number;
  hasMagic: boolean;
  /**
   * This fighter is stunned and has NO gesture this turn — the exchange goes to the
   * opponent whatever would have been chosen.
   *
   * It lives here, with every other legality rule, because "stunned" was the one
   * unavailability the rules file did not know about, and the gap showed: the player's
   * tray already collapsed to a single "Stunned!" button, while the AIs — which
   * hand-rolled their own legality — happily returned `recover` and the engine then
   * SPENT one of their limited recover uses on a turn they never got to act in.
   *
   * Optional and defaulting to false, so every caller that omits it is unchanged.
   */
  stunned?: boolean;
  recoverUses: number;
  maxRecoverUses: number;
  lastAction: CombatAction | null;
  sameActionStreak: number;
  /** Transient single-turn block flags. Default false (the frontend omits them). */
  blockedMagic?: boolean;
  blockedRecover?: boolean;
  /**
   * The player holds a recover card that consumes no recover use — Bloom, today.
   *
   * Without this, such a card is unplayable at exactly the moment it matters: the
   * recover GESTURE is rejected once uses are spent, so a card whose whole point is
   * that it costs no use can never be played as a last out.
   *
   * It lives here, on the one file that decides legality, rather than in
   * `cardRules.ts` — duplicating the exhaustion rule into the card layer is how the
   * two would silently diverge. Optional and defaulting to false, so every existing
   * caller (which omits it) behaves exactly as before.
   */
  hasFreeRecoverCard?: boolean;
  /**
   * **HOW MANY CARDS OF EACH SUIT THIS FIGHTER'S HAND HOLDS — and the rule that a move you
   * hold no cards for is not a move you may choose.**
   *
   * A bare commit used to be free: you could throw any gesture at any time, and the hand only
   * decided how BIG it landed. That made the hand a damage roll rather than a decision — the
   * five cards you were dealt never once changed which of the four buttons you were allowed to
   * press, so the pitch was "make my number bigger" and never "make my move possible".
   *
   * With this set, a suit your hand holds nothing of is off the table, and the two beats of a
   * round finally talk to each other: the pitch is where you buy your options, and the wheel is
   * played out of what you actually bought.
   *
   * **OPTIONAL, AND ABSENT MEANS OFF.** Every cards-off mode omits it and behaves exactly as it
   * always has — this file is consumed by battle modes that have no deck at all, and a rule
   * about cards must not fire where there are none.
   *
   * **IT NEVER STRANDS A FIGHTER.** If applying it would leave a fighter with no legal move at
   * all — four magic cards, a species with no magic, a spent dodge streak — the rule is waived
   * for that state rather than deadlocking the turn. See {@link cardRuleStrands}.
   *
   * **AND IT ONLY GOVERNS CHOICE, NEVER COERCION.** Force Magic still makes its target commit
   * magic with an empty magic hand; being forced into a bare commit is the card doing its job.
   * {@link getRedirectCandidates} therefore ignores this rule on purpose.
   */
  cardsInHand?: Partial<Record<CombatAction, number>>;
}

export interface ActionAvailability {
  available: boolean;
  reason?: ActionUnavailableReason;
}

/**
 * Reasons that BLOCK submission (the validator rejects them up front). All other
 * reasons are coerced/redirected during turn resolution instead of erroring —
 * keeping submit-time behavior identical to the historical `validateMove`.
 */
export const SUBMIT_REJECT_REASONS: ReadonlySet<ActionUnavailableReason> = new Set([
  'recover-exhausted',
  'dodge-streak',
  'no-magic',
  // A suit the hand holds nothing of is REJECTED, not redirected. Coercing it would hand the
  // player a move they did not pick on a turn they were told they could not pick it — and the
  // whole point of the rule is that the hand decides what is on the table.
  'no-cards',
]);

/**
 * Evaluate a single action. Reason precedence is ordered so the three
 * "reject-on-submit" reasons (no-magic, recover-exhausted, dodge-streak) take
 * priority over the coerce-only reasons (blocked, level-locked, action-locked),
 * which keeps `validateMove` behavior byte-identical to the legacy checks.
 */
function evaluateAction(
  s: CombatActionState,
  action: CombatAction,
  /**
   * Apply the hand rule ({@link CombatActionState.cardsInHand}).
   *
   * A parameter rather than a read off `s`, because two callers must switch it off: the
   * stranding check below (which asks what the rule would leave behind) and
   * {@link getRedirectCandidates} (coercion is exempt).
   */
  applyCardRule: boolean
): ActionAvailability {
  // stunned: no gesture at all this turn — checked FIRST because it outranks every other
  // reason. A stunned fighter is not choosing between legal moves, it is not choosing.
  if (s.stunned) {
    return { available: false, reason: 'stunned' };
  }

  // no-magic: species has no magic ability at all.
  if (action === 'magic' && !s.hasMagic) {
    return { available: false, reason: 'no-magic' };
  }

  // recover-exhausted: all per-battle recover uses are spent — UNLESS the player is
  // holding a recover card that spends no use (Bloom). See `hasFreeRecoverCard`.
  if (
    action === 'recover' &&
    s.recoverUses >= s.maxRecoverUses &&
    !s.hasFreeRecoverCard
  ) {
    return { available: false, reason: 'recover-exhausted' };
  }

  // dodge-streak: dodge may be repeated ONCE — the third consecutive dodge is out.
  // Dodge is answered cleanly by magic in the triangle, so back-to-back dodging is a
  // read the opponent can punish rather than a stall, and it no longer needs the
  // harder one-and-done brake it used to carry.
  if (
    action === 'dodge' &&
    s.lastAction === 'dodge' &&
    s.sameActionStreak >= ACTION_LOCK_LIMITS.dodge
  ) {
    return { available: false, reason: 'dodge-streak' };
  }

  // no-cards: the hand holds nothing of this suit, so there is no move to make with it.
  // Ranked below the three reasons above deliberately — "your species has no magic" and "your
  // recovers are spent" are facts about the FIGHTER and outrank a fact about this one hand,
  // which changes every round.
  if (applyCardRule && s.cardsInHand && (s.cardsInHand[action] ?? 0) <= 0) {
    return { available: false, reason: 'no-cards' };
  }

  // blocked: transient single-turn block effect.
  if (action === 'magic' && s.blockedMagic) {
    return { available: false, reason: 'blocked' };
  }
  if (action === 'recover' && s.blockedRecover) {
    return { available: false, reason: 'blocked' };
  }

  // level-locked: action not yet unlocked at this level (composes shared
  // levelUnlocks). With current thresholds every action is unlocked from
  // level 1 — kept here for completeness and future-proofing against new
  // level gates.
  if (!isActionUnlocked(action, s.level, s.hasMagic)) {
    return { available: false, reason: 'level-locked' };
  }

  // action-locked: too many consecutive uses of the same action.
  const limit = ACTION_LOCK_LIMITS[action as keyof typeof ACTION_LOCK_LIMITS];
  if (limit !== undefined && s.lastAction === action && s.sameActionStreak >= limit) {
    return { available: false, reason: 'action-locked' };
  }

  return { available: true };
}

/**
 * **WOULD THE HAND RULE LEAVE THIS FIGHTER WITH NOTHING TO PLAY?**
 *
 * A turn must always have a legal move. The hand rule alone cannot strand anybody — a hand of
 * five holds at most four copies of any one card, so it always spans two suits — but stacked on
 * the rules that were already here it can: four magic cards on a species with no magic and a
 * spent dodge streak leaves zero. A deadlocked turn is not a hard rule, it is a hung battle.
 *
 * So the rule is waived for exactly that state, and only then. Waiving it costs one bare commit
 * in a position that was already desperate; the alternative costs the fight.
 */
function cardRuleStrands(s: CombatActionState): boolean {
  return !ALL_COMBAT_ACTIONS.some((action) => evaluateAction(s, action, true).available);
}

/**
 * Is the hand rule live for this state?
 *
 * Off when the mode deals no cards (`cardsInHand` omitted), and off when it would strand the
 * fighter. Computed ONCE per state and shared by every entry point below, because a fighter
 * whose buttons the frontend enabled and whose submit the backend rejected is the exact class of
 * bug this file exists to prevent.
 */
function cardRuleApplies(s: CombatActionState): boolean {
  return s.cardsInHand !== undefined && !cardRuleStrands(s);
}

/** The single source of truth: availability + first failing reason for every action. */
export function getActionAvailability(
  s: CombatActionState
): Record<CombatAction, ActionAvailability> {
  const cards = cardRuleApplies(s);
  return {
    attack: evaluateAction(s, 'attack', cards),
    magic: evaluateAction(s, 'magic', cards),
    dodge: evaluateAction(s, 'dodge', cards),
    recover: evaluateAction(s, 'recover', cards),
  };
}

/** Is a specific action legal right now? */
export function isActionLegal(s: CombatActionState, action: CombatAction): boolean {
  return evaluateAction(s, action, cardRuleApplies(s)).available;
}

/** All currently-available actions, in canonical order. */
export function getAvailableActions(s: CombatActionState): CombatAction[] {
  const cards = cardRuleApplies(s);
  return ALL_COMBAT_ACTIONS.filter((a) => evaluateAction(s, a, cards).available);
}

/**
 * Candidate actions that a blocked/locked action may be REDIRECTED to, excluding
 * the rejected action itself, in canonical order. The caller (backend) supplies
 * the RNG selection over these candidates.
 *
 * `includeActionLock` defaults to FALSE so that an action that is unavailable
 * ONLY because of its own consecutive-use streak still counts as an eligible
 * redirect target — matching the legacy `getRedirectedAction` /
 * `getActionLockFallback`, which filtered legality (magic/recover/dodge/blocked)
 * but never excluded candidates by streak.
 */
export function getRedirectCandidates(
  s: CombatActionState,
  rejectedAction: CombatAction,
  options?: { includeActionLock?: boolean }
): CombatAction[] {
  const includeActionLock = options?.includeActionLock ?? false;
  return ALL_COMBAT_ACTIONS.filter((action) => {
    if (action === rejectedAction) return false;
    /*
     * **THE HAND RULE IS OFF HERE, DELIBERATELY.** A redirect is coercion, not choice: Force
     * Magic exists to make its target throw magic, and refusing to redirect them into a suit
     * they hold no cards of would be the card failing to do the one thing it does. They commit
     * it bare, which is the punishment.
     */
    const { available, reason } = evaluateAction(s, action, false);
    if (available) return true;
    // Treat a purely streak-locked action as still eligible unless asked otherwise.
    if (!includeActionLock && reason === 'action-locked') return true;
    return false;
  });
}
